import RedirectButton from 'components/atoms/redirect-button'
import TitlePage from 'components/atoms/title-page'
import CardLoader from 'components/organisms/card-loader'
import { ALERT_TYPES } from 'constants/alert-constants'
import SellerCard from 'domains/seller/organisms/seller-card'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { Card, Row, Col, Alert, Button, NavItem, NavLink, Nav } from 'reactstrap'
import { Link } from 'react-router-dom'
import ConsignmentInboundRequestsApi from 'api/v2/admins/consignment-inbound-requests-api'
import { SHIPPING_METHOD, STATUS } from '../constants'
import ConsignmentInboundRequestReceiveDropoffForm from '../receive-dropoff-form'
import ConsignmentInboundRequestReceivePickupForm from '../receive-pickup-form'
import ConsignmentInboundRequestCard from '../card'
import UserAddressList from 'domains/seller-inbound/organisms/user-address-list/user-address-list'
import ConsignmentInboudForm from 'domains/consignment-inbound/form/consignment-inbound-form'
import { BRAND_CLASSIFICATION } from 'domains/seller-product/constants'
import CatalogProductsApi from 'api/admins/catalog-products-api'
import ConsignmentInboundRequestSchedulePickupForm from '../schedule-pickup-form/consignment-inbound-request-schedule-pickup-form'
import ConsignmentInboundRequestConfirmForm from 'domains/consignment-inbound-request/confirm-form/consignment-inbound-request-confirm-form'
import { useModal } from 'providers/modal-provider'
import ConfirmationModal from 'components/organisms/confirmation-modal/confirmation-modal'
import ConsignmentInboundRequestCancelForm from '../cancel-form/consignment-inbound-request-cancel-form'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const TABS                              = {
  CONFIRM: {
    KEY   : 'confirm',
    LABEL : 'Confirm Consignment Inbound Request'
  },
  SCHEDULE: {
    KEY   : 'schedule',
    LABEL : 'Schedule Pickup Inbound'
  },
  RECEIVE: {
    KEY   : 'receive',
    LABEL : 'Receive Physical Inbound'
  },
  INBOUNDS: {
    KEY   : 'inbounds',
    LABEL : 'Update Consignment Inbounds'
  },
  CANCEL: {
    KEY   : 'cancel',
    LABEL : 'Cancel Consignment Inbound Request'
  }
}
const DEFAULT_TAB_FROM_STATUS           = {
  [SHIPPING_METHOD.DROPOFF]: {
    [STATUS.PENDING]   : TABS.CONFIRM,
    [STATUS.CONFIRMED] : TABS.RECEIVE,
    [STATUS.RECEIVED]  : TABS.INBOUNDS
  },
  [SHIPPING_METHOD.PICKUP]: {
    [STATUS.PENDING]   : TABS.CONFIRM,
    [STATUS.CONFIRMED] : TABS.SCHEDULE,
    [STATUS.SCHEDULED] : TABS.RECEIVE,
    [STATUS.RECEIVED]  : TABS.INBOUNDS
  }
}
const ACCEPTED_TL_BRAND_CLASSIFICATIONS = [
  BRAND_CLASSIFICATION.BRANDED,
  BRAND_CLASSIFICATION.LUXURY
]

const SellerCardWithLoader                    = withLoader(SellerCard, CardLoader)
const ConsignmentInboundRequestCardWithLoader = withLoader(ConsignmentInboundRequestCard, CardLoader)
const UserAddressListWithLoader               = withLoader(UserAddressList, CardLoader)

const ConsignmentInboundRequestDetailPage = ({ pageUtils }) => {
  const { showModal, hideModal }                                    = useModal()
  const [ consignmentInboundRequest, setConsignmentInboundRequest ] = useState({})
  const [ seller, setSeller ]                                       = useState({})
  const [ isLoading, setIsLoading ]                                 = useState(true)
  const { id }                                                      = useParams()
  const [ activeTab, setActiveTab ]                                 = useState(TABS.CONFIRM)
  const [ tlBrandClassifications, setTlBrandClassifications ]       = useState([])

  // Use ternary option to prevent boolean warning when condition is not true
  const navLinkClassName = (newActiveTab, tabName) => newActiveTab === tabName ? 'active' : null

  const handleTabChange = (tab) => setActiveTab(tab)

  /**
   * Handle get fetch all datas including `Consignment::InboundRequest`, `Seller::Inbound`, and `accepted TlBrandClassification`
   */
  useEffect(() => {
    CatalogProductsApi.getTlBrandClassification()
      .then((response) => {
        const tlBrandClassifications         = response.data.tl_brand_classifications
        const filteredTlBrandClassifications = tlBrandClassifications.filter(
          (brandClassification) => ACCEPTED_TL_BRAND_CLASSIFICATIONS.includes(brandClassification.name)
        )
        setTlBrandClassifications(filteredTlBrandClassifications)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))

    ConsignmentInboundRequestsApi.show({ id })
      .then((response) => {
        const consignmentInboundRequest = response.data.consignment_inbound_request
        setConsignmentInboundRequest(consignmentInboundRequest)
        setSeller(consignmentInboundRequest.seller)
        setIsLoading(false)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))

  }, [ id, pageUtils ])

  useEffect(() => {
    if (consignmentInboundRequest?.status) setActiveTab(DEFAULT_TAB_FROM_STATUS[consignmentInboundRequest.shipping_method]?.[consignmentInboundRequest.status] || TABS.CONFIRM)
  }, [ consignmentInboundRequest?.status ])

  /**
   * Handle submit to `ConsignmentInboundRequestReceiveForm`
   * @param {object} values final values when submitting the form.
   * @param {object} formikBag formik object
   */
  const handleSubmitReceiveForm = useCallback((values, formikBag) => {
    ConsignmentInboundRequestsApi.receive({
      id: id,
      ...values
    })
      .then((response) => {
        setConsignmentInboundRequest(response.data.consignment_inbound_request)
        pageUtils.setAlertMsg(
          'Inbound Request berhasil diterima.'
          , ALERT_TYPES.SUCCESS
        )
        formikBag.resetForm()
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }, [ id, pageUtils ])

  /**
   * Handle submit `ConsignmentInboundForm`
   * @param {object} values final values when submitting the form.
   * @param {object} formikBag formik object
   */
  const handleSubmitConsignmentInboundForm = useCallback((values, formikBag) => {
    ConsignmentInboundRequestsApi.updateInbounds( {
      consignment_inbound_request_id: id,
      values
    } )
      .then((response) => {
        setConsignmentInboundRequest(response.data.consignment_inbound_request)
        pageUtils.setAlertMsg(
          'Consignment Inbound berhasil disimpan.',
          ALERT_TYPES.SUCCESS
        )
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }, [ id, pageUtils ])

  const handleSchedulePickup = useCallback((values, formikBag) => {
    ConsignmentInboundRequestsApi.schedulePickup({
      id: id,
      ...values
    })
      .then((response) => {
        setConsignmentInboundRequest(response.data.consignment_inbound_request)
        pageUtils.setAlertMsg(
          'Pickup berhasil di-schedule.'
          , ALERT_TYPES.SUCCESS
        )
        formikBag.resetForm()
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }, [ id, pageUtils ])

  // Confirm Form
  /** TO CONFIRM INBOUND REQUEST STATUS SERVICE */
  const handleConfirmInbound = useCallback((values, formikBag) => {
    ConsignmentInboundRequestsApi.confirm({
      id: id,
      ...values
    })
      .then((response) => {
        setConsignmentInboundRequest(response.data.consignment_inbound_request)
        pageUtils.setAlertMsg(
          'Berhasil konfirmasi pickup inbound.',
          ALERT_TYPES.SUCCESS
        )
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => formikBag.setSubmitting(false))
  }, [ id, pageUtils ])

  const handleCancelInbound = useCallback((values, formikBag) => {
    ConsignmentInboundRequestsApi.cancel(values?.id)
      .then(() => {
        pageUtils.setAlertMsg(
          'Berhasil membatalkan request ini.',
          ALERT_TYPES.SUCCESS
        )
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => {
        formikBag.setSubmitting(false)
        hideModal()
      })
  }, [ hideModal, pageUtils ])

  const showCancelConfirmationModal = useCallback((values, formikBag) => {
    showModal(ConfirmationModal, {
      title              : 'Cancel Inbound Request',
      children           : `Apakah kamu yakin akan membatalkan request dengan code ${values?.code}?`,
      handleConfirmation : async () => handleCancelInbound(values, formikBag)
    })
  }, [ handleCancelInbound, showModal ])

  const receiveFormMap = useMemo(() => ({
    [ SHIPPING_METHOD.DROPOFF ]: (
      <ConsignmentInboundRequestReceiveDropoffForm
        handleSubmit={ handleSubmitReceiveForm }
        seller={ seller }
        consignmentInboundRequest={ consignmentInboundRequest }
      />
    ),
    [ SHIPPING_METHOD.PICKUP ]: (
      <ConsignmentInboundRequestReceivePickupForm
        handleSubmit={ handleSubmitReceiveForm }
        seller={ seller }
        consignmentInboundRequest={ consignmentInboundRequest }
      />
    )
  }), [ seller, consignmentInboundRequest, handleSubmitReceiveForm ])

  const renderActionForm = useCallback(() => {
    switch (activeTab) {
      case TABS.INBOUNDS:
        return (
          <ConsignmentInboudForm
            onSubmit={ handleSubmitConsignmentInboundForm }
            consignmentInboundRequest={ consignmentInboundRequest }
            tlBrandClassifications={ tlBrandClassifications }
          />
        )
      case TABS.RECEIVE:
        return  receiveFormMap[ consignmentInboundRequest.shipping_method ]
      case TABS.CONFIRM:
        return (
          <ConsignmentInboundRequestConfirmForm
            consignmentInboundRequest={ consignmentInboundRequest }
            onSubmit={ handleConfirmInbound }
          />
        )
      case TABS.SCHEDULE:
        return (
          <ConsignmentInboundRequestSchedulePickupForm
            handleSubmit={ handleSchedulePickup }
            consignmentInboundRequest={ consignmentInboundRequest }
          />
        )
      case TABS.CANCEL:
        return (
          <ConsignmentInboundRequestCancelForm
            onSubmit={ showCancelConfirmationModal }
            consignmentInboundRequest={ consignmentInboundRequest }
          />
        )
    }
  }, [ activeTab, consignmentInboundRequest, showCancelConfirmationModal, handleConfirmInbound, handleSchedulePickup, handleSubmitConsignmentInboundForm, receiveFormMap, tlBrandClassifications ])

  return (
    <>
      <RedirectButton
        className={ 'mb-3' }
        color='primary'
        size='sm'
        text={ '< Back to list' }
        path={ '/app/consignment/inbound-requests/list' }
      />

      <TitlePage mainTitle={ 'Consignment Inbound Request' } subTitle={ 'Detail' } />

      <Nav pills className='pb-3'>
        {
          Object.keys(TABS).map((key) => (
            <NavItem className='mr-3 pr-0' key={ `tab-${key}` }>
              <NavLink
                className={ navLinkClassName(activeTab, TABS[key]) }
                onClick={ () => { handleTabChange(TABS[key]) } }
              >
                <span className='text-uppercase'>{ TABS[key].KEY }</span>
              </NavLink>
            </NavItem>
          ))
        }
      </Nav>

      <h3>
        { activeTab.LABEL }
      </h3>
      <Row>
        <Col lg={ 6 } xs={ 12 }>
          <Card body>
            { renderActionForm() }
          </Card>
        </Col>
        <Col lg={ 3 } xs={ 12 }>
          <ConsignmentInboundRequestCardWithLoader
            isLoading={ isLoading }
            inboundRequest={ consignmentInboundRequest }
          />
        </Col>
        <Col lg={ 3 } xs={ 12 }>
          <SellerCardWithLoader
            isLoading={ isLoading }
            seller={ seller }
          />
          {
            seller.user ? (
              <UserAddressListWithLoader
                addresses={ seller.user?.addresses }
                isLoading={ isLoading }
              />
            ) : (
              <Alert color='info'>
                <div>
                  <i className='fas fa-info-circle mr-2' />
                  { 'Seller ini belum melakukan Account link.' }
                  <Button
                    color='success'
                    to={ `/app/customer-service/account-link?sellerEmail=${encodeURIComponent(seller?.email)}` }
                    tag={ Link }
                  >
                    { 'Account Link' }
                  </Button>
                </div>
              </Alert>
            )
          }
        </Col>
      </Row>
    </>
  )
}


ConsignmentInboundRequestDetailPage.propTypes = propTypes

export default ConsignmentInboundRequestDetailPage
